import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconShieldCheck } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { Image } from "../../../../components/common/image";
import { CurrencyAmount } from "../../../../components/common/currency_amount";
import type { SaleOrderLineDto } from "../../../../api";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { resolveProductRouteFromVariant } from "../../../products/utils/resolve-product-route";
import { showErrorToast } from "../../../../utils/toasts";

interface OrderItemsProps {
  orderLines: SaleOrderLineDto[];
  currency?: string;
  onFileClaim?: (lineId: number, productName: string) => void;
}

export function OrderItems({ orderLines, currency = "QAR", onFileClaim }: OrderItemsProps) {
  if (orderLines.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        <Heading variant="h3" tone="dark">Order Items</Heading>
        <div className="rounded-2xl border border-borderDefault bg-bgWhite px-5 py-6 text-center">
          <Text variant="bodySm" tone="muted">No items on this order.</Text>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <Heading variant="h3" tone="dark">Order Items</Heading>
      {orderLines.map((item) => (
        <OrderItemRow
          key={item.id}
          item={item}
          currency={currency}
          onFileClaim={onFileClaim && item.id != null ? () => onFileClaim(item.id!, item.product?.name ?? item.name ?? "") : undefined}
        />
      ))}
    </div>
  );
}

function OrderItemRow({ item, currency, onFileClaim }: { item: SaleOrderLineDto; currency: string; onFileClaim?: () => void }) {
  const productName = item.product?.name ?? item.name;
  const productSku = item.product?.id != null ? `ID-${item.product.id}` : null;
  const showSubtitle = !!item.name && item.name !== productName;
  const variantId = item.product?.id ?? null;
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);

  async function handleClick() {
    if (variantId == null || isNavigating) return;
    setIsNavigating(true);
    try {
      const href = await resolveProductRouteFromVariant(variantId, AppRoutes.PRODUCT_DETAILS);
      navigate(href);
    } catch {
      showErrorToast("Could not open product details.");
    } finally {
      setIsNavigating(false);
    }
  }

  const rowClasses = "flex items-start gap-4 rounded-2xl border border-borderDefault bg-bgWhite px-5 py-4";
  const rowContent = (
    <>
      <Image
        image={item.image ?? null}
        alt={productName}
        size="w-16 h-16"
        className="rounded-xl overflow-hidden shrink-0 mt-1"
      />
      <div className="flex flex-1 flex-col gap-0.5 text-left">
        <Text variant="bodyBold" tone="dark">{productName}</Text>
        {productSku && <Text variant="bodyXs" tone="muted">SKU: {productSku}</Text>}
        {showSubtitle && <Text variant="bodySm" tone="body">{item.name}</Text>}
        <div className="mt-2 border-t border-borderLight pt-2 flex items-center justify-between">
          <Text variant="bodySm" tone="muted">
            Quantity: <Text variant="bodySmBold" tone="dark" inline>{item.quantity}</Text>
          </Text>
          <CurrencyAmount
            amount={item.subtotal}
            currency={currency}
            variant="bodyBold"
            tone="dark"
          />
        </div>
      </div>
    </>
  );

  const row = variantId != null ? (
    <button
      type="button"
      onClick={handleClick}
      disabled={isNavigating}
      className={`${rowClasses} w-full hover:border-brandPrimary transition-colors ${isNavigating ? "opacity-60 cursor-wait" : ""}`}
    >
      {rowContent}
    </button>
  ) : (
    <div className={rowClasses}>{rowContent}</div>
  );

  if (!onFileClaim) return row;

  return (
    <div className="flex flex-col gap-1">
      {row}
      <div className="flex justify-end">
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<IconShieldCheck size={14} />}
          onClick={(e) => { e.stopPropagation(); onFileClaim(); }}
        >
          File Warranty Claim
        </Button>
      </div>
    </div>
  );
}
